import React, { CSSProperties, PropsWithChildren, ReactNode } from 'react'
import { T } from '@transifex/react'

import {
  DownCircleFilled,
  LeftCircleFilled,
  RightCircleFilled,
  UpCircleFilled,
} from 'src/icons/antd-compat'
import { cn } from 'src/lib/utils'

import type { EmptyProps } from './antd-props'
import Img from '../images/empty/Exclamation.png'
import List from '../images/empty/List.png'

/* -------------------------------------------------------------------------
 * The SHADCN half of the `src/antd/Empty` seam. `./Empty.tsx` is the
 * dispatcher that chooses between this and `./Empty.antd`; nothing else
 * imports this file, and nothing reaches it until someone flips the design
 * toggle (`src/design-mode`).
 *
 * Ported from the `update-design` reference branch's `src/antd/Empty.tsx`
 * (its Phase 2 slice 12). Divergences from the reference are marked
 * `[development]` below; everything else is the reference verbatim.
 *
 * WHAT THIS PORT IS NOT, and it is the same shape of caveat as `./Tag`: the
 * seam has SHADOWED Empty since long before the toggle existed. `./Empty.antd`
 * wraps antd's Empty in a full-height flex-centred div and substitutes this
 * repo's own placeholder art and its own "No data found" copy. So the thing
 * that must survive the flip is THAT — the wrapper, the PNG, the 50px image
 * height and the default description — not antd's default empty state.
 * `../__tests__/empty-seam-parity.test.tsx` compares against `./Empty.antd`
 * for exactly that reason, and the 12 files still importing `Empty` from raw
 * `antd` are the documented deferred residual (AGENTS.md); the toggle does not
 * reach them, by design.
 *
 * [development] THE TWO PRESET STATICS STAY ANTD'S OWN. The reference
 * transcribes `PRESENTED_IMAGE_SIMPLE` / `_DEFAULT` into hand-written SVG
 * because antd is deleted over there. Here they must keep their exact
 * identity, or the antd branch's DOM changes for the 52 call sites that pass
 * one back in as `image` — so `./Empty.tsx` forwards antd's own, unchanged, in
 * BOTH modes. Nothing is lost by that: antd's two illustrations paint from
 * inline `fill`/`stroke` attributes computed by `useToken()`
 * (`antd/es/empty/simple.js`), not from `.ant-empty-img-*` CSS, so they render
 * identically with antd's empty stylesheet absent — which it is whenever every
 * Empty on the page came through this branch.
 *
 * [development] Leftover props are SPREAD onto the state element rather than
 * dropped. The reference destructures a closed prop list; this repo passes
 * `data-testid` to `<Empty>` on three surfaces and `./Empty.antd` forwards it
 * (it spreads `{...props}` into antd's Empty), so dropping it here would fail
 * a test only in design mode.
 * ------------------------------------------------------------------------- */

const EmptyWrapper = ({ children }: PropsWithChildren<unknown>) => (
  <div className="flex h-full w-full items-center justify-center">
    {children}
  </div>
)

/**
 * antd's `image` prop is a ReactNode OR a URL string, and both forms are live
 * here: the seam's own default is an imported PNG (a string after the Vite
 * asset transform), while `Empty.PRESENTED_IMAGE_SIMPLE` is an element.
 */
const renderImage = (image: ReactNode, imageStyle?: CSSProperties) => {
  if (typeof image === 'string') {
    return (
      <img
        data-slot="empty-image"
        src={image}
        alt=""
        style={{ height: 50, ...imageStyle }}
      />
    )
  }
  return (
    <div data-slot="empty-image" style={imageStyle}>
      {image}
    </div>
  )
}

type TEmptyProps = EmptyProps & {
  withoutWrapper?: boolean
}

export const Empty = ({
  withoutWrapper = false,
  description,
  image = Img,
  imageStyle = { height: 50 },
  className,
  style,
  children,
  // antd-only props with no analogue here — dropped so they never hit the DOM.
  prefixCls: _prefixCls,
  rootClassName: _rootClassName,
  ...rest
}: TEmptyProps) => {
  const component = (
    <div
      data-slot="empty"
      className={cn(
        'flex flex-col items-center justify-center gap-2 text-center text-muted-foreground',
        className
      )}
      style={style}
      {...rest}
    >
      {renderImage(image, imageStyle)}
      <div data-slot="empty-description" className="text-sm">
        {description ?? (
          <span>
            <T _str="No data found" />
          </span>
        )}
      </div>
      {children}
    </div>
  )

  if (!withoutWrapper) return <EmptyWrapper>{component}</EmptyWrapper>
  return component
}

type EmptySelectItemProps = {
  direction?: 'top' | 'left' | 'right' | 'bottom'
  size?: 'default'
}

const sizeMap: Record<
  Exclude<EmptySelectItemProps['size'], undefined>,
  number
> = {
  default: 80,
}

export const EmptySelectItem = ({
  direction = 'left',
  size = 'default',
}: EmptySelectItemProps) => (
  <div
    data-slot="empty-select-item"
    className="flex flex-col items-center justify-center gap-2 text-center text-muted-foreground"
  >
    <img src={List} alt="" style={{ height: sizeMap[size] }} />
    <div className="inline-flex items-center gap-1 text-sm">
      {direction === 'bottom' && (
        <>
          <DownCircleFilled /> <T _str="Select an item at the bottom" />
        </>
      )}
      {direction === 'left' && (
        <>
          <LeftCircleFilled /> <T _str="Select an item on the left" />
        </>
      )}
      {direction === 'right' && (
        <>
          <RightCircleFilled /> <T _str="Select an item on the right" />
        </>
      )}
      {direction === 'top' && (
        <>
          <UpCircleFilled /> <T _str="Select an item at the top" />
        </>
      )}
    </div>
  </div>
)
